import React, {
	useMemo,
	useRef,
	useState,
	useEffect,
	useCallback,
} from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
	BoxIcon,
	DarkWhiteLogoIcon,
	GavelIcon,
	LogoLightNavIcon,
	LogoutIcon,
	MapPinIcon,
	MessageIcon,
	MoonIcon,
	ReportSearchIcon,
	SunWhiteIcon,
	TransportIcon,
	TrashIcon,
	UserCircleIcon,
	UserSquareIcon,
} from "@assets/icons";

import P from "@components/common/P/P";
import SvgIcon from "@components/common/SvgIcon/SvgIcon";
import { useTheme } from "hooks/useTheme";
import { useLocation, useNavigate } from "react-router-dom";
import NavItem from "./NavItem";
import { ROUTES } from "constants/routes";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "store/store";
import CompanyModal from "../CompanyModal/CompanyModal";
import { clearAuthState, updateUserCompanyId } from "store/slices/authSlice";
import { createCompany, fetchCompanyById } from "store/thunks/companyThunks";
import { uploadFile } from "@services/fileService";
import { clearCompanyState } from "store/slices/companySlice";
import { createClaim } from "@services/claimService";
import Input from "@components/common/Input/Input";
import Modal from "@components/common/Modal/Modal";
import Button from "@components/common/Button/Button";
import TextArea from "@components/common/TextArea/TextArea";
import { getRoleNameKey } from "@utils/getRoleName";

import LanguageSwitcher from "@components/common/LanguageSwitcher/LanguageSwitcher";
import { useTranslation } from "react-i18next";

const Navbar: React.FC = () => {
	const { t } = useTranslation();
	const { theme, toggleTheme } = useTheme();

	const company = useSelector((state: RootState) => state.company.data);
	const auth = useSelector((state: RootState) => state.auth);
	const dispatch = useDispatch<AppDispatch>();

	const location = useLocation();
	const navigate = useNavigate();

	const [isModalOpen, setIsModalOpen] = useState(false);
	const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

	const [companyName, setCompanyName] = useState("");
	const [companyAddress, setCompanyAddress] = useState("");
	const [phone, setPhone] = useState(auth.data.user?.phone || "");
	const [email, setEmail] = useState(auth.data.user?.email || "");
	const [image, setImage] = useState<string | Blob | null>(null);
	const fileInputRef = useRef<HTMLInputElement | null>(null);

	const [isDropdownOpen, setIsDropdownOpen] = useState(false);

	// ✅ separate refs
	const desktopDropdownRef = useRef<HTMLDivElement | null>(null);
	const mobileDropdownRef = useRef<HTMLDivElement | null>(null);

	const [isClaimOpen, setIsClaimOpen] = useState(false);
	const [claimForm, setClaimForm] = useState({
		name: "",
		email: auth.data.user?.email || "",
		phone: "",
		address: "",
		company_name: company?.company_name || "",
		subject: "",
		description: "",
		additional_details: "",
	});

	const roleId = company?.role_id;
	const isDriver = roleId === 4;
	const isSender = roleId === 3;

	const person = isDriver ? company?.drivers?.[0] : null;

	const avatarUrl =
		(isDriver ? person?.image_url : "") || company?.image_url || "";

	const initials = (first?: string, last?: string) =>
		[first?.charAt(0) && `${first.charAt(0)}.`, last].filter(Boolean).join(" ");

	const roleName = useMemo(() => t(getRoleNameKey(roleId)), [t, roleId]);

	const displayName = isDriver
		? initials(person?.first_name, person?.last_name) ||
			t("platform.nav.profile")
		: isSender
			? initials(company?.first_name, company?.last_name) ||
				t("platform.nav.profile")
			: company?.company_name || t("auth.register.register");

	const hasName = isDriver
		? Boolean(person?.first_name || person?.last_name)
		: isSender
			? Boolean(company?.first_name || company?.last_name)
			: Boolean(company?.company_name);

	const handleNavClose = useCallback(() => {
		setIsDropdownOpen(false);
		setIsMobileMenuOpen(false);
	}, []);

	// ✅ close first, then navigate
	const handleRouteNavigate = useCallback(
		(path: string) => {
			handleNavClose();
			requestAnimationFrame(() => {
				navigate(path);
			});
		},
		[handleNavClose, navigate],
	);

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			const target = event.target as Node;

			const clickedOutsideDesktop =
				desktopDropdownRef.current &&
				!desktopDropdownRef.current.contains(target);

			const clickedOutsideMobile =
				mobileDropdownRef.current &&
				!mobileDropdownRef.current.contains(target);

			if (clickedOutsideDesktop && clickedOutsideMobile) {
				setIsDropdownOpen(false);
			}
		};

		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, []);

	useEffect(() => {
		handleNavClose();
	}, [location.pathname, handleNavClose]);

	useEffect(() => {
		if (!isModalOpen) return;

		setPhone(auth.data.user?.phone || "");
		setEmail(auth.data.user?.email || "");
		setCompanyName(company?.company_name || "");
		setCompanyAddress(company?.address || "");
		setImage(company?.image_url || null);
	}, [
		isModalOpen,
		auth.data.user?.phone,
		auth.data.user?.email,
		company?.company_name,
		company?.address,
		company?.image_url,
	]);

	const openModal = useCallback(() => setIsModalOpen(true), []);
	const closeModal = useCallback(() => setIsModalOpen(false), []);

	const handleFileChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const file = e.target.files?.[0];
			if (file && file.type.startsWith("image/")) setImage(file);
		},
		[],
	);

	const openClaim = useCallback(() => {
		setIsClaimOpen(true);
		setIsDropdownOpen(false);
		setIsMobileMenuOpen(false);
	}, []);

	const closeClaim = useCallback(() => setIsClaimOpen(false), []);

	const onClaimChange = useCallback((k: string, v: string) => {
		setClaimForm((prev) => ({ ...prev, [k]: v }));
	}, []);

	const submitClaim = async () => {
		await createClaim({
			user_id: auth.data.user?.id as number,
			company_id: company?.id as number,
			name: claimForm.name,
			email: claimForm.email,
			phone: claimForm.phone,
			address: claimForm.address,
			company_name: claimForm.company_name,
			subject: claimForm.subject,
			description: claimForm.description,
			additional_details: claimForm.additional_details,
			claim_type: "damage_report",
			urgency_level: 4,
		});
		closeClaim();
	};

	const handleDeleteImage = useCallback(() => setImage(null), []);
	const handleEditImage = useCallback(() => fileInputRef.current?.click(), []);

	const handleSave = async () => {
		try {
			const token = auth.data.access_token;
			if (!token) {
				throw new Error("No access token available. Please log in again.");
			}

			let image_url = "";
			if (image instanceof File) {
				const uploadedFilePath = await uploadFile(image, token);
				if (!uploadedFilePath) {
					throw new Error("No file path returned from upload.");
				}
				image_url = uploadedFilePath;
			}

			const companyData = {
				user_id: auth.data.user?.id,
				role_id: auth.data.user?.role_id,
				role: auth.data.user?.role,
				company_name: companyName,
				address: companyAddress,
				phone,
				email,
				image_url,
				entity: "legal",
			};

			const response = await dispatch(createCompany(companyData)).unwrap();
			const newCompanyId = response.data.id;

			dispatch(updateUserCompanyId(newCompanyId));
			await dispatch(fetchCompanyById(newCompanyId)).unwrap();

			navigate(ROUTES.ACCOUNT);
			closeModal();
		} catch (error: any) {
			console.error("Error in handleSave:", error?.message || error);
			alert(t("platform.claim.toast.saveCompanyFailed"));
		}
	};

	const handleLogout = useCallback(() => {
		dispatch(clearAuthState());
		dispatch(clearCompanyState());
		handleNavClose();
		navigate(ROUTES.HOME);
	}, [dispatch, handleNavClose, navigate]);

	const handleCompanyClick = useCallback(() => {
		if (company) {
			setIsDropdownOpen((prev) => !prev);
			return;
		}
		openModal();
	}, [company, openModal]);

	const handleMobileMenuToggle = useCallback(() => {
		setIsMobileMenuOpen((prev) => !prev);
	}, []);

	const dropdownItems = useMemo(
		() => [
			{
				label: t("platform.dropdown.account"),
				icon: UserCircleIcon,
				action: () => handleRouteNavigate(ROUTES.ACCOUNT),
			},
			{
				label: t("landing.news.title"),
				icon: ReportSearchIcon,
				action: () => handleRouteNavigate(ROUTES.NEWS),
			},
			{
				label: t("platform.dropdown.leaveClaim"),
				icon: MessageIcon,
				action: openClaim,
			},
			{
				label: t("platform.dropdown.logout"),
				icon: LogoutIcon,
				action: handleLogout,
			},
			{
				label: t("platform.dropdown.deleteAccount"),
				icon: TrashIcon,
				action: handleLogout,
				danger: true,
			},
		],
		[handleLogout, handleRouteNavigate, openClaim, t],
	);

	const navItems = useMemo(() => {
		const baseItems = [
			...(company?.role_id === 6
				? [
						{
							icon: ReportSearchIcon,
							label: t("platform.nav.cargoRequests"),
							path: ROUTES.MY_REQUESTS,
						},
						{
							icon: ReportSearchIcon,
							label: t("platform.nav.transportRequests"),
							path: ROUTES.MY_OFFERS,
						},
					]
				: [
						{
							icon: ReportSearchIcon,
							label:
								company?.role_id === 3
									? t("platform.nav.cargoRequests")
									: t("platform.nav.transportRequests"),
							path:
								company?.role_id === 3 ? ROUTES.MY_REQUESTS : ROUTES.MY_OFFERS,
						},
					]),
			{
				icon: GavelIcon,
				label: t("platform.nav.myBids"),
				path: ROUTES.MY_BIDS,
			},
			{
				icon: MapPinIcon,
				label: t("platform.nav.deliveries"),
				path: ROUTES.DELIVERIES,
			},
			{
				icon: MessageIcon,
				label: t("platform.nav.chat"),
				path: ROUTES.CHAT,
			},
		];

		const searchItems =
			company?.role_id === 6
				? [
						{
							icon: TransportIcon,
							label: t("platform.nav.searchTransport"),
							path: ROUTES.TRANSPORT_SEARCH,
						},
						{
							icon: BoxIcon,
							label: t("platform.nav.searchCargo"),
							path: ROUTES.CARGO_SEARCH,
						},
					]
				: [
						{
							icon: company?.role_id === 3 ? TransportIcon : BoxIcon,
							label:
								company?.role_id === 3
									? t("platform.nav.searchTransport")
									: t("platform.nav.searchCargo"),
							path:
								company?.role_id === 3
									? ROUTES.TRANSPORT_SEARCH
									: ROUTES.CARGO_SEARCH,
						},
					];

		return [...searchItems, ...baseItems];
	}, [company?.role_id, t]);

	return (
		<header className="sticky top-0 z-40 bg-light-bgColor dark:bg-dark-bgColor border-b border-light-strokeLanding dark:border-dark-strokeLanding">
			<nav className="w-full">
				<div className="px-4 sm:px-6">
					<div className="flex items-center justify-between py-3">
						<div
							className="cursor-pointer shrink-0"
							onClick={() => handleRouteNavigate(ROUTES.HOME)}
						>
							<SvgIcon
								Icon={theme === "light" ? LogoLightNavIcon : DarkWhiteLogoIcon}
								width={92}
								height={32}
							/>
						</div>

						<div className="hidden md:flex items-center gap-3 lg:gap-4">
							<div className="relative" ref={desktopDropdownRef}>
								<div
									className="grid grid-cols-maxTwo place-items-center gap-2 bg-light-shadeOne dark:bg-dark-shadeOne py-2.5 px-3 lg:px-4 rounded-xl cursor-pointer"
									onClick={handleCompanyClick}
								>
									{avatarUrl ? (
										<img
											src={avatarUrl}
											alt={t("platform.nav.avatarAlt")}
											className="object-cover w-8 h-8 rounded-lg shrink-0"
										/>
									) : (
										<div className="grid grid-cols-1 p-1 rounded-lg bg-light-white dark:bg-dark-white shrink-0">
											<SvgIcon Icon={UserSquareIcon} />
										</div>
									)}

									<div className="grid grid-cols-1 gap-1 min-w-0">
										<P
											size="t1"
											type="semibold"
											className={`truncate cursor-pointer ${
												hasName
													? "dark:text-dark-textColor text-light-textColor"
													: "dark:text-dark-red text-light-red"
											}`}
										>
											{displayName}
										</P>

										<P
											size="t3"
											type="regular"
											className="truncate cursor-pointer text-light-grayThree dark:text-dark-grayThree"
										>
											{roleName}
										</P>
									</div>
								</div>

								<AnimatePresence>
									{isDropdownOpen && (
										<motion.div
											initial={{ opacity: 0, y: -10 }}
											animate={{ opacity: 1, y: 0 }}
											exit={{ opacity: 0, y: -10 }}
											transition={{ duration: 0.2 }}
											className="absolute top-full right-0 mt-2 w-60 bg-light-bgColor dark:bg-dark-bgColor rounded-xl shadow-lg border border-light-strokeLanding dark:border-dark-strokeLanding z-50"
										>
											<div className="p-2 space-y-2">
												{dropdownItems.map((item, index) => (
													<div
														key={index}
														onClick={(e) => {
															e.stopPropagation();
															item.action();
														}}
														className="p-2 rounded-md hover:bg-light-shadeOne dark:hover:bg-dark-shadeOne cursor-pointer transition-colors"
													>
														<div className="flex items-center gap-3">
															<SvgIcon
																Icon={item.icon}
																className={`w-5 h-5 ${
																	item.danger
																		? "stroke-light-red dark:stroke-dark-red"
																		: ""
																}`}
															/>
															<P
																size="t1"
																className={`cursor-pointer ${
																	item.danger
																		? "text-light-red dark:text-dark-red"
																		: "text-light-textColor dark:text-dark-textColor"
																}`}
																type="regular"
															>
																{item.label}
															</P>
														</div>
													</div>
												))}
											</div>
										</motion.div>
									)}
								</AnimatePresence>
							</div>

							<LanguageSwitcher variant="platform" mode="full" />

							<div
								className="bg-light-shadeOne dark:bg-dark-shadeOne p-2.5 rounded-full cursor-pointer shrink-0"
								onClick={toggleTheme}
								aria-label={t("common.toggleTheme")}
								title={t("common.toggleTheme")}
							>
								{theme === "light" ? (
									<SvgIcon
										Icon={MoonIcon}
										className="fill-light-textColor dark:fill-dark-textColor stroke-light-textColor dark:stroke-dark-textColor"
									/>
								) : (
									<SvgIcon Icon={SunWhiteIcon} />
								)}
							</div>
						</div>

						<div className="flex items-center gap-2 md:hidden">
							<button
								type="button"
								onClick={toggleTheme}
								aria-label={t("common.toggleTheme")}
								title={t("common.toggleTheme")}
								className="flex items-center justify-center rounded-full bg-light-shadeOne dark:bg-dark-shadeOne p-2.5"
							>
								{theme === "light" ? (
									<SvgIcon
										Icon={MoonIcon}
										className="fill-light-textColor dark:fill-dark-textColor stroke-light-textColor dark:stroke-dark-textColor"
									/>
								) : (
									<SvgIcon Icon={SunWhiteIcon} />
								)}
							</button>

							<button
								type="button"
								onClick={handleMobileMenuToggle}
								aria-label="Toggle navigation"
								className="flex h-11 w-11 items-center justify-center rounded-full bg-light-shadeOne dark:bg-dark-shadeOne"
							>
								<div className="relative flex h-4 w-5 flex-col justify-between">
									<span
										className={`block h-0.5 w-full rounded-full bg-light-textColor dark:bg-dark-textColor transition-all duration-300 ${
											isMobileMenuOpen ? "translate-y-[7px] rotate-45" : ""
										}`}
									/>
									<span
										className={`block h-0.5 w-full rounded-full bg-light-textColor dark:bg-dark-textColor transition-all duration-300 ${
											isMobileMenuOpen ? "opacity-0" : "opacity-100"
										}`}
									/>
									<span
										className={`block h-0.5 w-full rounded-full bg-light-textColor dark:bg-dark-textColor transition-all duration-300 ${
											isMobileMenuOpen ? "-translate-y-[7px] -rotate-45" : ""
										}`}
									/>
								</div>
							</button>
						</div>
					</div>
				</div>

				<div className="hidden md:block border-t border-light-strokeLanding dark:border-dark-strokeLanding">
					<div className="flex flex-wrap items-center justify-center gap-2 px-4 sm:px-6 py-2">
						{navItems.map(({ icon, label, path }) => (
							<NavItem
								key={path}
								icon={icon}
								path={path}
								label={label}
								isActive={location.pathname === path}
								onNavigate={handleNavClose}
							/>
						))}
					</div>
				</div>

				<AnimatePresence>
					{isMobileMenuOpen && (
						<motion.div
							initial={{ opacity: 0, height: 0 }}
							animate={{ opacity: 1, height: "auto" }}
							exit={{ opacity: 0, height: 0 }}
							transition={{ duration: 0.25 }}
							className="overflow-hidden border-t border-light-strokeLanding dark:border-dark-strokeLanding md:hidden"
						>
							<div className="px-4 py-4 space-y-4">
								<div className="relative" ref={mobileDropdownRef}>
									<div
										className="grid grid-cols-[auto,1fr] items-center gap-3 bg-light-shadeOne dark:bg-dark-shadeOne p-3 rounded-xl cursor-pointer"
										onClick={handleCompanyClick}
									>
										{avatarUrl ? (
											<img
												src={avatarUrl}
												alt={t("platform.nav.avatarAlt")}
												className="object-cover w-10 h-10 rounded-lg shrink-0"
											/>
										) : (
											<div className="grid grid-cols-1 p-2 rounded-lg bg-light-white dark:bg-dark-white shrink-0">
												<SvgIcon Icon={UserSquareIcon} />
											</div>
										)}

										<div className="min-w-0">
											<P
												size="t1"
												type="semibold"
												className={`truncate ${
													hasName
														? "dark:text-dark-textColor text-light-textColor"
														: "dark:text-dark-red text-light-red"
												}`}
											>
												{displayName}
											</P>
											<P
												size="t3"
												type="regular"
												className="truncate text-light-grayThree dark:text-dark-grayThree"
											>
												{roleName}
											</P>
										</div>
									</div>

									<AnimatePresence>
										{isDropdownOpen && (
											<motion.div
												initial={{ opacity: 0, y: -8 }}
												animate={{ opacity: 1, y: 0 }}
												exit={{ opacity: 0, y: -8 }}
												transition={{ duration: 0.2 }}
												className="mt-2 rounded-xl border border-light-strokeLanding dark:border-dark-strokeLanding bg-light-bgColor dark:bg-dark-bgColor shadow-lg"
											>
												<div className="p-2 space-y-1">
													{dropdownItems.map((item, index) => (
														<button
															key={index}
															type="button"
															onClick={item.action}
															className="flex w-full items-center gap-3 rounded-lg p-3 text-left hover:bg-light-shadeOne dark:hover:bg-dark-shadeOne transition-colors"
														>
															<SvgIcon
																Icon={item.icon}
																className={`w-5 h-5 ${
																	item.danger
																		? "stroke-light-red dark:stroke-dark-red"
																		: ""
																}`}
															/>
															<P
																size="t1"
																type="regular"
																className={
																	item.danger
																		? "text-light-red dark:text-dark-red"
																		: "text-light-textColor dark:text-dark-textColor"
																}
															>
																{item.label}
															</P>
														</button>
													))}
												</div>
											</motion.div>
										)}
									</AnimatePresence>
								</div>

								<div className="rounded-xl bg-light-shadeOne dark:bg-dark-shadeOne p-2">
									<LanguageSwitcher variant="platform" mode="full" />
								</div>

								<div className="grid grid-cols-1 gap-2">
									{navItems.map(({ icon, label, path }) => (
										<button
											key={path}
											type="button"
											onClick={() => handleRouteNavigate(path)}
											className={`flex items-center gap-3 rounded-xl px-4 py-3 transition-colors ${
												location.pathname === path
													? "bg-light-shadeOne dark:bg-dark-shadeOne"
													: "bg-transparent"
											}`}
										>
											<SvgIcon Icon={icon} className="w-5 h-5 shrink-0" />
											<P
												size="t1"
												type="medium"
												className="text-light-textColor dark:text-dark-textColor"
											>
												{label}
											</P>
										</button>
									))}
								</div>
							</div>
						</motion.div>
					)}
				</AnimatePresence>
			</nav>

			<Modal
				isOpen={isClaimOpen}
				onClose={closeClaim}
				header={t("platform.claim.header")}
			>
				<div className="grid grid-cols-1 gap-3">
					<Input
						label={t("platform.claim.name")}
						value={claimForm.name}
						onChange={(e) => onClaimChange("name", e.target.value)}
					/>
					<Input
						label={t("platform.claim.email")}
						value={claimForm.email}
						onChange={(e) => onClaimChange("email", e.target.value)}
					/>
					<Input
						label={t("platform.claim.phone")}
						value={claimForm.phone}
						onChange={(e) => onClaimChange("phone", e.target.value)}
					/>
					<Input
						label={t("platform.claim.address")}
						value={claimForm.address}
						onChange={(e) => onClaimChange("address", e.target.value)}
					/>
					<Input
						label={t("platform.claim.company")}
						value={claimForm.company_name}
						onChange={(e) => onClaimChange("company_name", e.target.value)}
					/>
					<Input
						label={t("platform.claim.subject")}
						value={claimForm.subject}
						onChange={(e) => onClaimChange("subject", e.target.value)}
					/>
					<TextArea
						label={t("platform.claim.description")}
						value={claimForm.description}
						onChange={(e) => onClaimChange("description", e.target.value)}
					/>
					<Input
						label={t("platform.claim.additional")}
						value={claimForm.additional_details}
						onChange={(e) =>
							onClaimChange("additional_details", e.target.value)
						}
					/>

					<div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-2">
						<Button appearance="secondary" onClick={closeClaim}>
							{t("platform.claim.cancel")}
						</Button>
						<Button appearance="primary" onClick={submitClaim}>
							{t("platform.claim.send")}
						</Button>
					</div>
				</div>
			</Modal>

			<CompanyModal
				isOpen={isModalOpen}
				onClose={closeModal}
				companyName={companyName}
				setCompanyName={setCompanyName}
				companyAddress={companyAddress}
				setCompanyAddress={setCompanyAddress}
				phone={phone}
				setPhone={setPhone}
				email={email}
				setEmail={setEmail}
				loginType={auth.data.user?.phone?.trim() ? "phone" : "email"}
				image={image}
				handleFileChange={handleFileChange}
				handleEditImage={handleEditImage}
				handleDeleteImage={handleDeleteImage}
				handleSave={handleSave}
				fileInputRef={fileInputRef}
			/>
		</header>
	);
};

export default React.memo(Navbar);
